import {
  ASIAN_LANGUAGE_PREFIXES,
  ASIAN_PAYMENT_COUNTRY_CODES,
  ASIAN_PAYMENT_TIMEZONES,
  CREATE_IMAGE_MODEL_NAME,
  EDIT_IMAGE_MODEL_NAME,
  NUDIFY_MODEL_NAME,
  QWEN_Z_MODEL_NAME,
  QWEN_Z_TWO_INPUT_IMAGES,
  QWEN_Z_THREE_INPUT_IMAGES,
  TEXT_ON_BODY_MODEL_NAME,
  UPSCALE,
} from "./constants";

export const capitalizeWords = (str) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export const priceFormatter = (price) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
};

export const priceFormatterDecimal = (price) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price);
};

export const formatDate = (dateString, onlyDate) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return onlyDate
    ? `${month} ${day}, ${year}`
    : `${day} ${month} ${year}, ${hours}:${minutes}h`;
};

export const formatTemplateBadge = (count) => {
  if (!count) return "No templates";

  if (count === 1) return "1 template";
  if (count <= 9) return `${count} templates`;
  if (count < 20) return "10+ templates";

  const tens = Math.floor(count / 10) * 10;
  return `${tens}+ templates`;
};

export const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return "just now";
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? "minute" : "minutes"} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? "hour" : "hours"} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) {
    return `${diffInDays} ${diffInDays === 1 ? "day" : "days"} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} ${diffInMonths === 1 ? "month" : "months"} ago`;
  }

  const diffInYears = Math.floor(diffInMonths / 12);
  return `${diffInYears} ${diffInYears === 1 ? "year" : "years"} ago`;
};

export const daysDifference = (date) => {
  const now = new Date();
  const target = new Date(date);

  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  const diffTime = target - now;
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return `In ${diffDays} day${diffDays > 1 ? "s" : ""}`;
  } else if (diffDays < 0) {
    return `Expired`;
  } else {
    return "Today";
  }
};

// Allowed email domains for registration
const ALLOWED_EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "icloud.com",
  "protonmail.com",
  "proton.me",
  "aol.com",
  "live.com",
  "msn.com",
];

export const isEmailAllowed = (email) => {
  if (!email || typeof email !== "string") {
    return false;
  }

  // Basic email format validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return false;
  }

  // Extract domain from email
  const domain = email.toLowerCase().split("@")[1];

  // Check if domain is in allowed list
  return ALLOWED_EMAIL_DOMAINS.includes(domain);
};

export const getAllowedEmailDomains = () => {
  return [...ALLOWED_EMAIL_DOMAINS];
};

export const getEmailValidationMessage = (email) => {
  if (!email) {
    return "Email is required";
  }

  if (!isEmailAllowed(email)) {
    const domain = email.includes("@") ? email.split("@")[1] : "";
    if (domain && !ALLOWED_EMAIL_DOMAINS.includes(domain.toLowerCase())) {
      return `Sorry, ${domain} is not supported. Please use one of these email providers: ${ALLOWED_EMAIL_DOMAINS.join(
        ", ",
      )}`;
    }
    return "Please enter a valid email address";
  }

  return null; // Email is valid
};

export function isBoolean(val) {
  return val === false || val === true || val instanceof Boolean;
}

export function formatModelName(str) {
  const NO_EDIT_WORDS = ["POV"];
  return str
    .split(/[_ ]/)
    .map((word) => {
      if (!NO_EDIT_WORDS.includes(word)) {
        return word.charAt(0) + word.toLowerCase().slice(1);
      } else {
        return word;
      }
    })
    .join(" ");
}

export function isImageOutput(modelName) {
  const imageOutputModels = [
    NUDIFY_MODEL_NAME,
    TEXT_ON_BODY_MODEL_NAME,
    EDIT_IMAGE_MODEL_NAME,
    QWEN_Z_MODEL_NAME,
    QWEN_Z_TWO_INPUT_IMAGES,
    QWEN_Z_THREE_INPUT_IMAGES,
    UPSCALE,
    "NUDIFY",
    CREATE_IMAGE_MODEL_NAME,
  ];

  if (!modelName) {
    return false;
  }

  return imageOutputModels.includes(modelName);
}

export const getMonthName = (monthNumber) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  if (monthNumber < 1 || monthNumber > 12) {
    return "unknown month";
  }

  return monthNames[monthNumber - 1];
};

export const getOrCreateBrowserId = () => {
  try {
    const cookieName = "pb_browser_id";

    const existing = document.cookie
      .split("; ")
      .find((row) => row.startsWith(cookieName + "="));

    if (existing) {
      return decodeURIComponent(existing.split("=")[1]);
    }

    const newId = crypto.randomUUID();

    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 2);

    const hostname = window.location.hostname;
    const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1";

    const parts = [
      `${cookieName}=${encodeURIComponent(newId)}`,
      "path=/",
      `expires=${expires.toUTCString()}`,
      "SameSite=Lax",
    ];

    if (!isLocalhost) {
      parts.push("domain=.playbun.com");
      parts.push("Secure");
    }

    document.cookie = parts.join("; ");

    return newId;
  } catch (err) {
    console.log("GET browserId error", err);
  }
};

export const navigateToTop = (navigate, path) => {
  navigate(path);
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
};

// Whether to show the AliPay / WeChat Pay payment options, based on the
// user's country code, timezone OR browser language being Asian.
export const showAsianPayments = (user) => {
  try {
    const countryCode = (user?.countryCode || "").toUpperCase();
    const countryHit = ASIAN_PAYMENT_COUNTRY_CODES.includes(countryCode);

    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const timezoneHit = ASIAN_PAYMENT_TIMEZONES.includes(timezone);

    const languages =
      navigator.languages?.length > 0
        ? navigator.languages
        : [navigator.language];

    const languageHit = languages.some((language) => {
      const lang = (language || "").toLowerCase();
      return ASIAN_LANGUAGE_PREFIXES.some(
        (prefix) => lang === prefix || lang.startsWith(`${prefix}-`),
      );
    });

    return countryHit || timezoneHit || languageHit;
  } catch {
    return false;
  }
};
